import { Component, OnDestroy, OnInit } from '@angular/core';
import { RmsModule } from '@rms';
import { Column, HeaderButton } from '@interfaces';
import { LedgerService, RmsMessageService, RmsPulldownService } from '@services';
import { JournalFormComponent } from '../journal-form/journal-form.component';
import { dateOnly, exportLedgerExcel } from '../ledger-export';

@Component({
  selector: 'app-recycle-bin',
  imports: [RmsModule],
  templateUrl: './recycle-bin.component.html',
  styleUrl: './recycle-bin.component.scss'
})
export class RecycleBinComponent implements OnInit, OnDestroy {
  Filters = { DeletedFrom: '', DeletedThrough: '', SearchText: '' };
  Rows: any[] = [];
  LegacyRows: any[] = [];
  Loading = false;
  ShowLegacy = false;
  LegacyLoaded = false;
  private request = 0;

  Columns: Column[] = [
    { label: 'Draft', key: 'JournalNumber', width: 8 },
    { label: 'Deleted By', key: 'DeletedByName', width: 12 },
    { label: 'Deleted On', key: 'DeletedDisplay', width: 12 },
    { label: 'Reason', key: 'DeleteReason', width: 16 },
    { label: 'Retention', key: 'RetentionDisplay', width: 8 },
    { label: 'Posting Date', key: 'EntryDate', type: 'date', width: 9 },
    { label: 'Source Reference', key: 'SourceReference', width: 13 },
    { label: 'Debits', key: 'DebitTotal', type: 'currency', width: 9, align: 'right' },
    { label: 'Eligibility', key: 'RestoreEligibility', width: 16, cellClass: (row: any) => row.CanRestore ? 'restore-ok' : 'restore-blocked' }
  ];

  Buttons: HeaderButton[] = [
    { label: 'Export', icon: 'download', class: 'btn-bg-blue', onAction: () => this.export() }
  ];

  LegacyColumns: Column[] = [
    { label: 'Entry', key: 'TransactionID', width: 8 },
    { label: 'Date', key: 'TransactionDate', type: 'date', width: 9 },
    { label: 'Account', key: 'AccountDisplay', width: 18 },
    { label: 'Customer / Payee', key: 'PartyDisplay', width: 18 },
    { label: 'Reference', key: 'ReferenceDisplay', width: 13 },
    { label: 'Deposit', key: 'DepositAmount', type: 'currency', width: 10, align: 'right' },
    { label: 'Withdrawal', key: 'WithdrawalAmount', type: 'currency', width: 10, align: 'right' }
  ];

  constructor(private ledger: LedgerService, private message: RmsMessageService, private pulldown: RmsPulldownService) { }

  ngOnInit() { this.load(); }

  ngOnDestroy() { ++this.request; }

  load() {
    const request = ++this.request;
    this.Loading = true;
    this.ledger.getRecycleBin(this.Filters).subscribe({
      next: (rows) => {
        if (request !== this.request) return;
        this.Loading = false;
        // Accounting keeps deleted drafts forever, so there is no purge date
        this.Rows = (rows || []).map((r: any) => ({
          ...r,
          DeletedDisplay: r.DeletedDate ? new Date(r.DeletedDate).toLocaleString() : '—',
          RetentionDisplay: 'Indefinite'
        }));
      },
      error: (err) => {
        if (request !== this.request) return;
        this.Loading = false;
        this.message.error('Failed to load the recycle bin: ' + this.errorText(err));
      }
    });
  }

  toggleLegacy() {
    this.ShowLegacy = !this.ShowLegacy;
    if (!this.ShowLegacy || this.LegacyLoaded) return;
    this.ledger.getDeletedHistory().subscribe({
      next: (rows) => {
        this.LegacyLoaded = true;
        this.LegacyRows = (rows || []).map((r: any) => ({
          ...r,
          AccountDisplay: r.AccountNumber ? `${r.AccountNumber} ${r.AccountName}` : '—',
          PartyDisplay: r.CompanyName || r.PayorOrPayee || '—',
          ReferenceDisplay: [
            r.RoyalNYInvoiceNumber ? `Inv ${r.RoyalNYInvoiceNumber}` : '',
            r.MiscellaneousInvoiceNumber ? `Misc ${r.MiscellaneousInvoiceNumber}` : '',
            r.CheckNumber ? `Chk ${r.CheckNumber}` : ''
          ].filter(Boolean).join(' · ') || '—'
        }));
      },
      error: (err) => this.message.error('Failed to load legacy deletions: ' + this.errorText(err))
    });
  }

  open(row: any) {
    const ref = this.pulldown.open(JournalFormComponent, {
      title: `Journal ${row.JournalNumber} (recycle bin)`,
      data: { JournalEntryID: row.JournalEntryID },
      width: '85vw',
      maxWidth: '95vw',
      maxHeight: '92vh',
      top: '0'
    });
    ref.afterClosed().subscribe(() => this.load());
  }

  get FilterSummary(): string {
    return [
      this.Filters.DeletedFrom ? 'Deleted from ' + this.Filters.DeletedFrom : '',
      this.Filters.DeletedThrough ? 'Deleted through ' + this.Filters.DeletedThrough : '',
      this.Filters.SearchText ? 'Search: ' + this.Filters.SearchText : ''
    ].filter(Boolean).join(' | ') || 'All deleted drafts';
  }

  async export() {
    if (!this.Rows.length) { this.message.warn('The recycle bin is empty.'); return; }
    await exportLedgerExcel({
      title: 'Recycle Bin',
      fileName: 'journal-recycle-bin',
      filterSummary: this.FilterSummary,
      summary: [['Deleted drafts', this.Rows.length]],
      columns: [
        { header: 'Draft', value: r => r.JournalNumber, width: 10 },
        { header: 'Deleted By', value: r => r.DeletedByName, width: 20 },
        { header: 'Deleted On', value: r => r.DeletedDisplay, width: 22 },
        { header: 'Reason', value: r => r.DeleteReason, width: 32 },
        { header: 'Retention', value: r => r.RetentionDisplay, width: 12 },
        { header: 'Posting Date', value: r => dateOnly(r.EntryDate), width: 12 },
        { header: 'Journal Reason', value: r => r.Reason, width: 30 },
        { header: 'Source Reference', value: r => r.SourceReference, width: 26 },
        { header: 'Debits', value: r => r.DebitTotal, width: 14, money: true },
        { header: 'Credits', value: r => r.CreditTotal, width: 14, money: true },
        { header: 'Prepared By', value: r => r.PreparedByName, width: 20 },
        { header: 'Eligibility', value: r => r.RestoreEligibility, width: 30 }
      ],
      rows: this.Rows
    });
  }

  private errorText(err: any): string {
    return err?.error?.error || err?.message || 'Unknown error';
  }
}
